import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { fetchRfqs } from '@/api/rfqs';
import { PriceAction } from '@/features/rfqs/PriceAction';
import { RFQ_STATUS_COLOUR } from '@/features/rfqs/statuses';
import type { RfqStatus } from '@/types/generated';

const STATUSES: { value: RfqStatus | ''; label: string }[] = [
  { value: '', label: 'Any status' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'expired', label: 'Expired' },
];

/**
 * The RFQ queue.
 *
 * Defaults to OPEN ONLY, unlike the customer and catalogue lists. Those are
 * reference data someone browses; this is a work queue, and a queue that shows
 * every request ever raised is not one.
 */
export function RfqsPage() {
  const [status, setStatus] = useState<RfqStatus | ''>('');
  const [openOnly, setOpenOnly] = useState(true);

  const query = useInfiniteQuery({
    queryKey: ['rfqs', { status, openOnly }],
    queryFn: ({ pageParam }) =>
      fetchRfqs({ status: status || undefined, openOnly, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
  });

  const rfqs = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <Typography variant="h5">Requests</Typography>
        <Button component={RouterLink} to="/rfqs/new" variant="contained" size="small" startIcon={<AddIcon />}>
          New request
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(event) => setStatus(event.target.value as RfqStatus | '')}
          sx={{ minWidth: 170 }}
        >
          {STATUSES.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <FormControlLabel
          control={
            <Switch size="small" checked={openOnly} onChange={(event) => setOpenOnly(event.target.checked)} />
          }
          label="Still open"
        />
      </Stack>

      {query.isError && <Alert severity="error">Could not load requests.</Alert>}

      <Card variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Reference</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Route</TableCell>
              <TableCell align="right">Chargeable</TableCell>
              <TableCell align="right">Distance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {query.isPending && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            )}

            {!query.isPending && rfqs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {openOnly ? 'Nothing open right now.' : 'No requests yet.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {rfqs.map((rfq) => (
              <TableRow key={rfq.id} hover>
                <TableCell>
                  <Box
                    component={RouterLink}
                    to={`/rfqs/${rfq.id}`}
                    sx={{ color: 'primary.main', textDecoration: 'none' }}
                  >
                    {rfq.reference}
                  </Box>
                  {rfq.has_lapsed && <Chip size="small" label="past due" color="error" sx={{ ml: 1 }} />}
                </TableCell>
                <TableCell>{rfq.customer_name}</TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {rfq.origin_city ?? rfq.origin_description} → {rfq.destination_city ?? rfq.destination_description}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {rfq.chargeable_weight_kg === null ? (
                    '—'
                  ) : (
                    // The basis, not just the number: a chargeable weight far above
                    // the gross weight reads as a bug until you know volume won.
                    <Tooltip
                      title={
                        rfq.chargeable_weight_basis === 'volumetric'
                          ? 'Charged on volume'
                          : rfq.chargeable_weight_basis === 'actual'
                            ? 'Charged on gross weight'
                            : 'Volume does not apply to this cargo'
                      }
                    >
                      <span>
                        {Math.round(rfq.chargeable_weight_kg).toLocaleString()} kg
                        {rfq.chargeable_weight_basis === 'volumetric' && ' ⓥ'}
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right">
                  {rfq.distance_km === null ? (
                    '—'
                  ) : (
                    // Provenance matters: an estimate and a routed distance are not
                    // equally trustworthy pricing inputs.
                    <Tooltip
                      title={
                        rfq.distance_source === 'geodesic_estimate'
                          ? 'Straight-line estimate × 1.25'
                          : rfq.distance_source === 'operator'
                            ? 'Entered by hand'
                            : 'From a routing engine'
                      }
                    >
                      <span>
                        {Math.round(rfq.distance_km).toLocaleString()} km
                        {rfq.distance_source === 'geodesic_estimate' && ' ≈'}
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={rfq.status} color={RFQ_STATUS_COLOUR[rfq.status]} />
                </TableCell>
                <TableCell align="right">
                  <PriceAction rfq={rfq} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {query.hasNextPage && (
        <Box>
          <Button
            variant="outlined"
            size="small"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
